import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete, MenuItem } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearEmplazamientosPPE = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    centcodigo: "",
    emplcodigo: "",
    empldescri: "",
    emplstatus: "A",
  })

  // --- LÓGICA DEFENSIVA PARA CENTROS DE PPE ---
  const { data: rawCentros, isLoading: isLoadingCentros } = useQuery({
    queryKey: ["listaCentrosPPE_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/EmplazamientosPPE/getListaCentrosPPE")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Centros de PPE:", error)
        return []
      }
    },
  })

  const centrosValidados = Array.isArray(rawCentros)
    ? rawCentros
    : Array.isArray(rawCentros?.data)
      ? rawCentros.data
      : []
  const listaCentros = centrosValidados.map((item) => ({
    id: item.centcodigo || item.id,
    label: item.label || `${item.centcodigo} - ${item.centdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveCreacionEmplazamiento,
    isPending: isSavingCreacion,
    isError,
  } = useMutation({
    queryKey: ["isCreatingEmplazamiento"],
    fn: async (data) => {
      const response = await api.post("/EmplazamientosPPE/createEmplazamientosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "emplcodigo") finalValue = finalValue.slice(0, 10)
    else if (field === "empldescri") finalValue = finalValue.slice(0, 100)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del frontend
    if (!formData.centcodigo.trim()) return showWarning("Debe seleccionar un Centro de PPE")
    if (!formData.emplcodigo.trim()) return showWarning("El Código del Emplazamiento es obligatorio")
    if (!formData.empldescri.trim()) return showWarning("La Descripción del Emplazamiento es obligatoria")

    await SaveCreacionEmplazamiento(formData)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) =>
      action?.acccaption === "GUARDAR" ||
      action?.acccaption === "EJECUTAR" ||
      action?.acccaption === "CREAR" ||
      action?.acccaption === "NUEVO",
  )

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacion || isLoadingCentros}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nuevo Emplazamiento de PPE</b>
        </div>

        <CustomBackdrop isLoading={isSavingCreacion || isLoadingCentros} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Emplazamiento
            </Typography>

            <Grid container spacing={3}>
              {/* COMBO CENTRO DE PPE */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCentros}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaCentros.find((c) => c.id === formData.centcodigo) ||
                    (formData.centcodigo ? { id: formData.centcodigo, label: `${formData.centcodigo}` } : null)
                  }
                  onChange={(event, newValue) =>
                    setFormData((prev) => ({ ...prev, centcodigo: newValue ? newValue.id : "" }))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Centro Padre *"
                      placeholder="Buscar centro..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.centcodigo}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO EMPLAZAMIENTO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Emplazamiento *"
                  value={formData.emplcodigo}
                  onChange={(e) => handleInputChange("emplcodigo", e.target.value)}
                  error={isError && !formData.emplcodigo}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* DESCRIPCIÓN EMPLAZAMIENTO */}
              <Grid item xs={12} sm={5}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.empldescri}
                  onChange={(e) => handleInputChange("empldescri", e.target.value)}
                  error={isError && !formData.empldescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearEmplazamientosPPE
